import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { PublicQuestionnaireService } from './public-questionnaire.service';

/**
 * External taxpayer questionnaire form (CU-868kg6qux) — the native, in-SPA
 * replacement for the Qount-hosted forms.taxplaniq.com page. Reached at
 * `questionnaire-form/:token` with NO route guard: the token is the credential,
 * the taxpayer has no login, and neither LoggedInActivator (would redirect to
 * /login) nor WallDashBoardActivator (would redirect a signed-in firm user) may
 * run. All identity is resolved server-side from the token.
 */
@Component({
  selector: 'app-public-questionnaire-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './public-questionnaire-form.component.html',
  styleUrls: ['./public-questionnaire-form.component.scss']
})
export class PublicQuestionnaireFormComponent implements OnInit {

  token: string;
  loading = true;
  error = '';
  saving = false;
  // stopgap cap under the 10 MiB API Gateway limit (CU-868kqe2zn)
  readonly maxFileSizeMb = 9;

  firmName = '';
  logoUrl = '';
  clientFirstName = '';
  bucket = '';
  lock = false;
  done = false;
  submitted = false;

  sections: any[] = [];
  activeIndex = 0;
  // questions for the active section, keyed by question type for rendering
  questions: any[] = [];
  // monotonic id of the newest section request; older responses are ignored
  private sectionRequestSeq = 0;

  constructor(
    private route: ActivatedRoute,
    private qs: PublicQuestionnaireService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token');
    if (!this.token) {
      this.fail('This questionnaire link is invalid.');
      return;
    }
    this.qs.bootstrap(this.token).subscribe(
      (res: any) => {
        this.firmName = res.firmName || '';
        this.logoUrl = res.logoUrl || '';
        this.clientFirstName = res.clientFirstName || res.contactFirstName || '';
        this.bucket = res.bucket || '';
        this.lock = !!res.lock;
        this.done = !!res.done;
        this.loadQuestions();
      },
      () => this.fail('This questionnaire link is invalid or has expired.')
    );
  }

  private loadQuestions(): void {
    this.qs.getQuestions(this.token, this.bucket).subscribe(
      (res: any) => {
        this.sections = res.sections || [];
        this.applySection(res.questions);
        this.loading = false;
      },
      () => this.fail('We could not load your questionnaire. Please try again later.')
    );
  }

  private applySection(payload: any): void {
    const answers = (payload && payload.questionsAnswer) || [];
    answers.sort((a: any, b: any) => a.questionNo - b.questionNo);
    this.questions = answers;
  }

  get activeSection(): any {
    return this.sections[this.activeIndex] || {};
  }

  get isLastSection(): boolean {
    return this.activeIndex >= this.sections.length - 1;
  }

  get readOnly(): boolean {
    return this.lock || this.done || this.submitted;
  }

  selectSection(index: number): void {
    if (index === this.activeIndex || index < 0 || index >= this.sections.length) {
      return;
    }
    // Persist the current section before leaving it, so switching tabs never
    // discards the taxpayer's unsaved answers. Read-only forms just navigate.
    if (this.readOnly) {
      this.loadSection(index);
      return;
    }
    this.saving = true;
    this.qs.saveAnswers(this.token, this.buildPayload()).subscribe(
      () => {
        this.saving = false;
        this.loadSection(index);
      },
      () => {
        this.saving = false;
        this.error = 'We could not save your answers. Please try again.';
      }
    );
  }

  private loadSection(index: number): void {
    const seq = ++this.sectionRequestSeq;
    this.qs.getSectionQuestions(this.token, this.sections[index].id, this.bucket).subscribe(
      (res: any) => {
        // Ignore a stale response: the user selected a newer section while this
        // request was in flight, so it must not overwrite the newer selection.
        if (seq !== this.sectionRequestSeq) {
          return;
        }
        // Switch the active section only after its questions load. Otherwise a
        // failed request leaves the prior section's answers bound to the new
        // section id, and a later save writes them into the wrong section.
        this.applySection(res);
        this.activeIndex = index;
      },
      () => {
        if (seq !== this.sectionRequestSeq) {
          return;
        }
        this.fail('We could not load that section.');
      }
    );
  }

  /**
   * MM/DD/YYYY (stored) -> YYYY-MM-DD for the native date input. Returns '' for a
   * value that is not a real calendar date (bad month/day, or 02/29 in a
   * non-leap year), so a malformed legacy answer shows an empty picker.
   */
  toIsoDate(mdy: string): string {
    if (!mdy) {
      return '';
    }
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(mdy.trim());
    if (!m) {
      return '';
    }
    const month = +m[1], day = +m[2], year = +m[3];
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return '';
    }
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  /** Native date input (YYYY-MM-DD) -> MM/DD/YYYY for storage. */
  onDateChange(question: any, event: any): void {
    const iso = event && event.target ? event.target.value : '';
    if (!iso) {
      question.dateAnswer = '';
      return;
    }
    const parts = iso.split('-');
    question.dateAnswer = `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  toggleCheckbox(question: any, option: any): void {
    if (this.readOnly) {
      return;
    }
    option.checked = !option.checked;
  }

  private buildPayload(): any {
    return {
      sectionId: this.activeSection.id,
      sectionName: this.activeSection.name,
      questionBucket: this.bucket,
      questionsAnswer: this.questions
    };
  }

  saveAndProceed(): void {
    if (this.readOnly) {
      return;
    }
    this.saving = true;
    const savedIndex = this.activeIndex;
    this.qs.saveAnswers(this.token, this.buildPayload()).subscribe(
      () => {
        this.saving = false;
        // Advance only when the saved section is still active, so a tab change
        // during the save cannot skip a section or jump from another one. Load
        // directly (not selectSection) because this section is already saved.
        if (this.activeIndex === savedIndex && savedIndex < this.sections.length - 1) {
          this.loadSection(savedIndex + 1);
        }
      },
      () => { this.saving = false; this.error = 'We could not save your answers. Please try again.'; }
    );
  }

  submit(): void {
    if (this.readOnly) {
      return;
    }
    this.saving = true;
    this.qs.submitAnswers(this.token, this.buildPayload()).subscribe(
      () => { this.saving = false; this.submitted = true; },
      () => { this.saving = false; this.error = 'We could not submit your questionnaire. Please try again.'; }
    );
  }

  onFileSelected(event: any, question: any): void {
    const input = event.target;
    const file: File = input && input.files && input.files[0];
    if (!file) {
      return;
    }
    // Clear the input on every terminal result so the taxpayer can re-select
    // the same file. Otherwise no change event fires on the second attempt.
    const reset = () => { if (input) { input.value = ''; } };
    if (!this.isAllowedFile(file)) {
      this.error = `${file.name} is not an accepted file type (PDF, Word, or image only).`;
      reset();
      return;
    }
    // Stopgap cap under the 10 MiB API Gateway limit (CU-868kqe2zn); the gateway
    // rejects a larger request before our server can. Keep this in sync with the
    // backend FileTypeValidator.MAX_FILE_SIZE.
    if (file.size > this.maxFileSizeMb * 1024 * 1024) {
      this.error = `${file.name} exceeds the ${this.maxFileSizeMb} MB limit. Please upload a file no larger than ${this.maxFileSizeMb} MB.`;
      reset();
      return;
    }
    const form = new FormData();
    form.append('file', file, file.name);
    question.uploading = true;
    this.http.post(this.qs.uploadUrl(this.token, question.questionId, this.bucket), form).subscribe(
      (res: any) => {
        question.uploading = false;
        question.attachments = question.attachments || [];
        question.attachments.push({ id: res.documentID, name: res.documentName });
        reset();
      },
      (err: any) => {
        question.uploading = false;
        // 413 = the gateway rejected an oversized payload; name the size problem
        this.error = err && err.status === 413
          ? `${file.name} is too large. Please upload a file no larger than ${this.maxFileSizeMb} MB.`
          : 'We could not upload that file.';
        reset();
      }
    );
  }

  removeAttachment(question: any, index: number): void {
    const docId = question.attachments[index].id;
    this.qs.deleteDocument(this.token, question.questionId, docId).subscribe(
      () => {
        // Remove by id, not the captured index: a concurrent delete can shift
        // the array and make splice(index) drop the wrong attachment.
        const at = question.attachments.findIndex((a: any) => a.id === docId);
        if (at !== -1) {
          question.attachments.splice(at, 1);
        }
      },
      () => this.error = 'We could not remove that file.'
    );
  }

  private isAllowedFile(file: File): boolean {
    const allowedExt = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'heic'];
    const allowedMime = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png', 'image/jpeg', 'image/heic'
    ];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (allowedExt.indexOf(ext) === -1) {
      return false;
    }
    // When the browser reports a MIME type, require it to match the allowlist so
    // a renamed unsupported file is rejected here, not only at the server. Some
    // browsers send an empty type for HEIC, so keep the blank fallback for HEIC.
    if (file.type) {
      return allowedMime.indexOf(file.type.toLowerCase()) !== -1;
    }
    return ext === 'heic';
  }

  private fail(message: string): void {
    this.error = message;
    this.loading = false;
  }
}
